/// <reference path="./types.d.ts" />

/**
 * Initialize Telegram WebApp using native API
 * Must be called before accessing initData
 */
export const initTelegramWebApp = () => {
  try {
    // Check if running inside Telegram
    if (typeof window === 'undefined' || !window.Telegram?.WebApp) {
      console.error('❌ Telegram WebApp API not available');
      console.error('This should never happen in production TMA');
      return false;
    }

    const tg = window.Telegram.WebApp;
    
    // Use native Telegram WebApp API
    tg.ready();
    tg.expand();
    
    // Set header color if available
    if (tg.themeParams?.bg_color) {
      try {
        tg.setHeaderColor(tg.themeParams.bg_color);
      } catch (e) {
        console.warn('Could not set header color:', e);
      }
    }
    
    console.log('✅ Telegram WebApp initialized successfully');
    console.log('📱 Platform:', tg.platform);
    console.log('📦 Version:', tg.version);
    console.log('🎨 Color Scheme:', tg.colorScheme);
    console.log('🔐 InitData available:', !!tg.initData && tg.initData.length > 0);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Telegram WebApp:', error);
    return false;
  }
};

const parseInitDataFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const rawFromSearch = searchParams.get("tgWebAppData");
  const rawFromHash = hashParams.get("tgWebAppData");
  const rawValue = rawFromSearch || rawFromHash;
  if (!rawValue) {
    return null;
  }
  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
};

/**
 * Get Telegram initData for authentication
 * Returns null if not available or if there's an error
 */
export const getInitData = () => {
  try {
    // Telegram WebApp should always be available in TMA
    if (typeof window === 'undefined' || !window.Telegram?.WebApp) {
      console.error('❌ Telegram WebApp not available - this should not happen!');
      return null;
    }

    const tg = window.Telegram.WebApp;
    
    // Log detailed debug info
    console.log('=== Telegram WebApp Debug Info ===');
    console.log('Platform:', tg.platform);
    console.log('Version:', tg.version);
    console.log('InitData length:', tg.initData?.length || 0);
    console.log('InitData (first 100 chars):', tg.initData?.substring(0, 100) || 'EMPTY');
    console.log('InitDataUnsafe:', JSON.stringify(tg.initDataUnsafe, null, 2));
    console.log('ViewportHeight:', tg.viewportHeight);
    console.log('IsExpanded:', tg.isExpanded);
    console.log('ColorScheme:', tg.colorScheme);
    
    // Check if opened via direct link
    const urlParams = new URLSearchParams(window.location.search);
    const hasStartParam = urlParams.has('tgWebAppStartParam');
    console.log('Has tgWebAppStartParam:', hasStartParam);
    console.log('URL:', window.location.href);
    console.log('================================');
    
    const initData = tg.initData || parseInitDataFromUrl();
    
    if (!initData || initData.length === 0) {
      console.error('❌ InitData is empty!');
      console.error('');
      console.error('Приложение открыто в Telegram, но initData отсутствует.');
      console.error('');
      console.error('🔍 ДИАГНОСТИКА:');
      console.error('- Telegram Version:', tg.version);
      console.error('- Platform:', tg.platform);
      console.error('- URL:', window.location.href);
      console.error('- Has StartParam:', hasStartParam);
      console.error('');
      console.error('Возможные причины:');
      console.error('1. ❌ Приложение открыто по прямой ссылке (не через Menu Button)');
      console.error('2. ❌ Menu Button не настроен в @BotFather');
      console.error('3. ❌ Используется старая версия Telegram (обновите приложение)');
      console.error('4. ❌ URL в Menu Button не совпадает с текущим URL');
      console.error('');
      console.error('✅ ПРАВИЛЬНЫЙ СПОСОБ ОТКРЫТЬ:');
      console.error('1. Откройте бота в Telegram');
      console.error('2. Найдите кнопку ☰ (Menu Button) внизу поля ввода');
      console.error('3. Нажмите на неё - приложение откроется с initData');
      console.error('');
      console.error('⚙️ НАСТРОЙКА MENU BUTTON:');
      console.error('1. Откройте @BotFather в Telegram');
      console.error('2. Отправьте: /mybots');
      console.error('3. Выберите вашего бота');
      console.error('4. Bot Settings → Menu Button → Configure Menu Button');
      console.error('5. Введите URL: https://tma.tarifdata.com');
      console.error('6. Закройте и снова откройте бота');
      console.error('7. Нажмите кнопку ☰');
      console.error('');
      
      return null;
    }
    
    console.log('✅ InitData retrieved successfully (length:', initData.length, ')');
    return initData;
  } catch (error) {
    console.error('❌ Failed to get init data:', error);
    return null;
  }
};

export const getInitDataWithRetry = async (
  retries = 8,
  delayMs = 200,
) => {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const value = getInitData();
    if (value) {
      return value;
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
};
